// src/components/admin/scheduling/student_calendar/StudentCalendarGrid.js
//
// Shared calendar-grid components extracted from StudentCalendar.js.
// Imported by both StudentCalendar.js (full-page calendar) and
// ModalCalendarPanel.js (calendar embedded in Create/Edit Session modal).
// DO NOT add any data-fetching or Firestore logic here — pure display only.

import React, { useMemo } from "react";
import {
  monthGridDays,
  isSameMonth,
  sameYMD,
  isDisabledDay,
  ymd,
} from "../../employee/availability/availabilityUtils";

// Re-export ymd so callers can import it from this module too
export { ymd };

// ─── Status color map (matches AdminScheduling.css / Modal.css) ───────────────
export const STATUS_META = {
  pending:   { bg: "#FFFFD7", border: "#d4d090", tutorColor: "#78610a" },
  confirmed: { bg: "#ECFFE2", border: "#84c970", tutorColor: "#166534" },
  present:   { bg: "#E6F5FF", border: "#93c5fd", tutorColor: "#1e40af" },
  "no-show": { bg: "#DFDFDF", border: "#b8b8b8", tutorColor: "#57534e" },
  cancelled: { bg: "#FFE8E8", border: "#f9a8a8", tutorColor: "#991b1b" },
  canceled:  { bg: "#FFE8E8", border: "#f9a8a8", tutorColor: "#991b1b" },
};

export function getStatusMeta(status) {
  const k = String(status || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
  return STATUS_META[k] || { bg: "#f5f7ff", border: "#dde3f6", tutorColor: "#07219D" };
}

// ─── Pure helpers ─────────────────────────────────────────────────────────────

export function toDate(ts) {
  if (!ts) return null;
  if (typeof ts.toDate === "function") return ts.toDate();
  return ts instanceof Date ? ts : new Date(ts);
}

export function fmtTime(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return "--";
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export function isCancelledStatus(status) {
  const v = String(status || "").trim().toLowerCase();
  return v === "cancelled" || v === "canceled";
}

function toMidnightMillis(value) {
  if (!value) return NaN;
  const d = value instanceof Date ? new Date(value) : new Date(value);
  if (isNaN(d.getTime())) return NaN;
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function isPastDateValue(value) {
  const dayMs   = toMidnightMillis(value);
  const todayMs = toMidnightMillis(new Date());
  if (!Number.isFinite(dayMs) || !Number.isFinite(todayMs)) return false;
  return dayMs < todayMs;
}

export function monthKey(d) {
  return `${d.getFullYear()}-${d.getMonth()}`;
}

// ─── DayCell ──────────────────────────────────────────────────────────────────
// Identical rendering to StudentCalendar.js's DayCell.
// Props:
//   date, month, idx, today
//   sessionsByDay, selectedDate, selectedSessionId
//   dayConfigsMap, isDeveloperMode
//   onDayClick(date, isClosed)
//   onSessionClick(sessId, isAlreadySelected)
//   onNoteEnter(note, rect), onNoteLeave()

export function DayCell({
  date, month, idx, today,
  sessionsByDay, selectedDate, selectedSessionId,
  dayConfigsMap, isDeveloperMode,
  onDayClick, onSessionClick,
  onNoteEnter, onNoteLeave,
  // Optional drag-and-drop props — used by ModalCalendarPanel; not passed by full-page calendars
  onSessionDragStart, draggingSessionId,
  onDayDragOver, onDayDrop,
  dragOverDate,
}) {
  const inMonth = isSameMonth(date, month);

  if (!inMonth) {
    return (
      <div style={{
        height: 100,
        background: "#fff",
        borderBottom: "none",
        borderRight: "none",
        pointerEvents: "none",
      }} />
    );
  }

  const hasPrevInMonth  = idx > 0 && isSameMonth(new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1), month);
  const hasAboveInMonth = isSameMonth(new Date(date.getFullYear(), date.getMonth(), date.getDate() - 7), month);
  const key             = ymd(date);
  const isClosed        = isDisabledDay(date, dayConfigsMap);
  const isToday         = sameYMD(date, today);
  const isSel           = selectedDate && sameYMD(selectedDate, date);
  const isPast          = !isDeveloperMode && isPastDateValue(date);
  const cellSessions    = sessionsByDay.get(key) || [];
  const visibleSessions = cellSessions.slice(0, 2);
  const hiddenCount     = Math.max(0, cellSessions.length - 2);
  const isMulti         = cellSessions.length > 1;
  const isDragTarget    = !!onDayDragOver && dragOverDate === ymd(date) && !isClosed;

  return (
    <div
      onClick={() => onDayClick(date, isClosed)}
      onDragOver={onDayDragOver ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; onDayDragOver(date); } : undefined}
      onDrop={onDayDrop ? (e) => { e.preventDefault(); onDayDrop(date); } : undefined}
      style={{
        height: 100,
        background: isDragTarget ? "#eff6ff" : isSel ? "#f9fafb" : isClosed ? "#f0f2f5" : "#fff",
        borderLeft:   hasPrevInMonth  ? "none" : "1px solid #e5e7eb",
        borderTop:    hasAboveInMonth ? "none" : "1px solid #e5e7eb",
        borderBottom: "1px solid #e5e7eb",
        borderRight:  "1px solid #e5e7eb",
        padding: "6px 7px 5px",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        cursor: isClosed ? "default" : "pointer",
        boxShadow: isDragTarget
          ? "inset 0 0 0 2px #2563eb"
          : isSel ? "inset 0 0 0 1.5px #1f2937" : "none",
        transition: "background-color 0.1s, box-shadow 0.1s",
        overflow: "hidden",
      }}
    >
      {/* Date number */}
      <div style={{
        alignSelf: "flex-end",
        height: 24,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        marginBottom: 4,
        flexShrink: 0,
      }}>
        <div style={{
          width: isToday ? 22 : "auto",
          height: isToday ? 22 : "auto",
          borderRadius: "50%",
          background: isToday ? "#1f2937" : "transparent",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: isToday ? 0 : "0 2px",
        }}>
          <span style={{
            fontSize: 12,
            fontWeight: isToday ? 700 : 500,
            color: isToday ? "#fff" : isClosed ? "#b0b8c4" : "#374151",
            lineHeight: 1,
          }}>
            {date.getDate()}
          </span>
        </div>
      </div>

      {/* Session chips */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2, overflow: "hidden" }}>
        {!isClosed && visibleSessions.map(sess => {
          const isSelected   = sess.id === selectedSessionId;
          const meta         = getStatusMeta(sess.status);
          const isCancelled  = isCancelledStatus(sess.status);
          const hasNote      = !!sess.note?.trim();
          const start        = toDate(sess.start);
          const end          = toDate(sess.end);
          const timeStr      = `${fmtTime(start)} – ${fmtTime(end)}`;
          const isDragging   = sess.id === draggingSessionId;
          const canDrag      = !!onSessionDragStart && !sess.isDraftPreview && !isPast && !isCancelled;

          return (
            <div
              key={sess.id}
              draggable={canDrag}
              onDragStart={canDrag ? (e) => {
                e.stopPropagation();
                e.dataTransfer.effectAllowed = "move";
                onSessionDragStart(sess);
              } : undefined}
              onClick={(e) => { e.stopPropagation(); onSessionClick(sess.id, isSelected); }}
              onMouseEnter={(e) => hasNote && onNoteEnter && onNoteEnter(sess.note, e.currentTarget.getBoundingClientRect())}
              onMouseLeave={() => onNoteLeave && onNoteLeave()}
              style={{
                background: meta.bg,
                border: isSelected
                  ? "1.5px dashed #111827"
                  : `1px solid ${meta.border}`,
                borderLeft: hasNote
                  ? "3px solid #335586"
                  : isSelected
                    ? "1.5px dashed #111827"
                    : `1px solid ${meta.border}`,
                borderRadius: 5,
                padding: isMulti ? "2px 5px" : "3px 6px",
                cursor: canDrag ? "grab" : "pointer",
                opacity: isDragging ? 0.35 : isCancelled ? 0.55 : isPast ? 0.7 : 1,
                flexShrink: 0,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              {/* Tutor name */}
              <div style={{
                fontSize: isMulti ? 9 : 10,
                fontWeight: 700,
                color: meta.tutorColor,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.35,
              }}>
                {sess.tutorName || "—"}
              </div>
              {/* Time */}
              <div style={{
                fontSize: isMulti ? 9 : 10,
                fontWeight: 700,
                color: "#111827",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.35,
              }}>
                {timeStr}
              </div>
              {/* Subject — only when single session */}
              {!isMulti && (
                <div style={{
                  fontSize: 9,
                  fontWeight: 500,
                  color: "#6b7280",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1.35,
                }}>
                  {sess.subject || "No Subject"}
                </div>
              )}
            </div>
          );
        })}

        {hiddenCount > 0 && (
          <span style={{ fontSize: 9, color: "#9ca3af", fontWeight: 500, lineHeight: 1, padding: "1px 2px" }}>
            +{hiddenCount} more
          </span>
        )}
      </div>
    </div>
  );
}

// ─── MonthSection ─────────────────────────────────────────────────────────────
// Identical rendering to StudentCalendar.js's MonthSection.

// compact=true switches cell columns from minmax(106px,1fr) to minmax(0,1fr),
// preventing horizontal overflow when embedded in a constrained panel (e.g. modal).
export function MonthSection({ month, monthRef, isCurrentMonth, compact = false, ...cellProps }) {
  const days  = useMemo(() => monthGridDays(month), [month]);
  const weeks = useMemo(() => {
    const rows = [];
    for (let i = 0; i < days.length; i += 7) {
      const week = days.slice(i, i + 7);
      if (week.some(d => isSameMonth(d, month))) rows.push(week);
    }
    return rows;
  }, [days, month]);

  const colTemplate = compact
    ? "repeat(7, minmax(0, 1fr))"
    : "repeat(7, minmax(106px, 1fr))";

  return (
    <div data-month-key={monthKey(month)} ref={monthRef} style={{ scrollMarginTop: 38 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, padding: "26px 0 12px" }}>
        <span style={{ fontSize: 20, fontWeight: 800, color: "#111827", letterSpacing: "-0.5px" }}>
          {month.toLocaleDateString("en-US", { month: "long" })}
        </span>
        <span style={{ fontSize: 13, fontWeight: 400, color: "#9ca3af" }}>
          {month.getFullYear()}
        </span>
        {isCurrentMonth && (
          <span style={{
            fontSize: 9, fontWeight: 700, textTransform: "uppercase",
            letterSpacing: "0.5px", color: "#6b7280",
            border: "1px solid #d1d5db", borderRadius: 100,
            padding: "2px 8px", lineHeight: 1,
          }}>
            This Month
          </span>
        )}
      </div>

      {weeks.map((week, wi) => (
        <div key={wi} style={{ display: "grid", gridTemplateColumns: colTemplate }}>
          {week.map((date, idx) => (
            <DayCell key={`${ymd(date)}-${idx}`} date={date} month={month} idx={idx} {...cellProps} />
          ))}
        </div>
      ))}
    </div>
  );
}
